import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getCustomer, getCustomerOrders } from '../../api/customers'
import { type Order } from '../../api/orders'
import { DataTable, type Column } from '@/components/DataTable'
import { DetailSection } from '@/components/DetailSection'
import { PageHeader } from '@/components/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<'info' | 'orders'>('info')

  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id!),
    enabled: !!id,
  })

  const { data: orders } = useQuery({
    queryKey: ['customer-orders', id],
    queryFn: () => getCustomerOrders(id!),
    enabled: !!id && activeTab === 'orders',
  })

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Lade Kunde…</div>
  if (isError || !customer) return (
    <div className="p-6">
      <p className="text-sm text-destructive">Kunde nicht gefunden.</p>
    </div>
  )

  const orderColumns: Column<Order>[] = [
    {
      key: 'number',
      header: 'Auftrag',
      cell: (o) => (
        <Link to={`/orders/${encodeURIComponent(o.number)}`} className="font-mono text-xs text-primary hover:underline">
          {o.number}
        </Link>
      ),
    },
    {
      key: 'date',
      header: 'Datum',
      cell: (o) => o.date ? new Date(o.date).toLocaleDateString('de-DE') : '—',
    },
    {
      key: 'invoice_amount',
      header: 'Betrag',
      className: 'text-right font-mono',
      cell: (o) => o.invoice_amount ? `${Number(o.invoice_amount).toFixed(2)} €` : '—',
    },
  ]

  return (
    <div className="p-6 max-w-3xl">
      <PageHeader
        title={customer.username ?? customer.email}
        breadcrumb={[{ label: 'Kunden', href: '/customers' }]}
      />
      <Tabs defaultValue="info" onValueChange={(v) => setActiveTab(v as 'info' | 'orders')}>
        <TabsList>
          <TabsTrigger value="info">Stammdaten</TabsTrigger>
          <TabsTrigger value="orders">Aufträge</TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="mt-4">
          <DetailSection
            rows={[
              { label: 'E-Mail', value: customer.email, copyable: true },
              { label: 'Username', value: customer.username },
              { label: 'Firma', value: customer.company },
              { label: 'Telefon', value: customer.phone },
              { label: 'Erstellt', value: new Date(customer.created_at).toLocaleString('de-DE') },
            ].filter((r) => r.value !== undefined && r.value !== null)}
          />
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <DataTable
            data={orders ?? []}
            columns={orderColumns}
            rowKey={(o) => o.id}
            isLoading={!orders && activeTab === 'orders'}
            emptyMessage="Keine Aufträge gefunden."
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
